import { useNavigate } from "react-router-dom"
import { useSelector } from "react-redux"
import { useState, useEffect } from "react"
import { useTour } from "../../context/TourContext.js"
import { useHintProgress } from "../../hooks/useHintProgress.js"
import { NUDGE_CONFIG, tourSteps } from "../../utils/tutorialSteps.js"
import { TutorialPopover } from "./index.2.0.jsx"
import { useMediaQuery } from "@custom-react-hooks/use-media-query"
import { useFeatureLock } from "../../hooks/useFeatureLock.js"

export const HintNudgeController = () => {
    const isDesktop = useMediaQuery(process.env.REACT_APP_IS_DESKTOP);
    const navigate = useNavigate()
    const { getEntry, subscribe } = useTour()
    const { setHintProgress } = useHintProgress()
    const hintProgress = useSelector(state => state.hintProgress)
    const { chatLocked } = useFeatureLock()
    const dayNumber = useSelector(state => state.loginDay)
    const onboardingStep = useSelector(state => state.onboardingStep ?? "welcome")

    const onboardingDone = onboardingStep === "chat-page" || onboardingStep === "onboarding-complete"

    const lastNudgeShownDay = hintProgress.lastNudgeShownDay
    const [, forceTick] = useState(0)

    const resolveResponsive = (value, fallback) => {
        if (Array.isArray(value)) {
            const [mobile, desktop] = value
            const resolved = isDesktop ? desktop : mobile
            return resolved !== undefined ? resolved : fallback
        }
        return value !== undefined ? value : fallback
    }

    useEffect(() => {
        return subscribe(() => forceTick(t => t + 1))
    }, [subscribe])

    const config = NUDGE_CONFIG.find(c => !hintProgress[c.shownFlag])
    const canShowToday = !lastNudgeShownDay || dayNumber > lastNudgeShownDay
    const isDayFourNudge = config?.shownFlag === "nudgeDayFourShown"

    if (!onboardingDone || !config || !canShowToday || (isDayFourNudge && !chatLocked)) return null

    let entry = null
    if (config.target) {
        entry = getEntry(config.target)
        if (!entry) return null
    }

    const allRelatedHintsSeen = config.relatedHintFlags.every(f => hintProgress[f])
    const shouldNavigate = config.relatedHintFlags.length > 0 && !allRelatedHintsSeen

    return (
        <TutorialPopover
            description={shouldNavigate ? config.message : config.alternative}
            anchorRef={entry ? { current: entry.el } : { current: null }}
            onNext={async () => {
                entry?.onNext?.()
                await setHintProgress(config.shownFlag)
                await setHintProgress("lastNudgeShownDay", dayNumber)
                if (shouldNavigate) navigate(config.navigateToPath, { state: { triggerHints: true } })
            }}
            type="nudge"
            dismissable={config.dismissable ?? false}
            placement={resolveResponsive(config.placement, "top")}
            spotlightPadding={resolveResponsive(config.padding, 8)}
            spotlightMode={resolveResponsive(config.spotlightMode, "spotlight")}
            className={resolveResponsive(config.className, "")}
            hideArrow={resolveResponsive(config.hideArrow, false)}
            top={resolveResponsive(config.top, 0)}
            bottom={resolveResponsive(config.bottom, 0)}
            right={resolveResponsive(config.right, 0)}
            left={resolveResponsive(config.left, 0)}
            disableScroll={resolveResponsive(config.disableScroll, false)}
            delay={resolveResponsive(config.delay, 600)}
            config={resolveResponsive(config.config, 0)}
            totalSteps={resolveResponsive(config.totalSteps, 5)}
            buttonContent={(!shouldNavigate || isDayFourNudge) ? config.alternativeButtonContent : null}
        />
    )
}